import { useResponsive } from '@/contexts/ResponsiveContext';
import { isSection } from '@/utils/managerAuditData';
import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react';
import { useAuditData } from './useAuditData';

const initCurrentView = {
  section: null,
  answer: null,
  action: null,
  issue: null,
  show: false,
  showSection: false,
  showAnswer: false,
  showIssue: false,
};

const reducer = (state, act) => {
  switch (act?.type) {
    case 'update': {
      const { section, answer, action, issue } = act?.value;
      const showIssue = !!issue;
      const showAnswer = !issue && !!answer;
      const showSection = !answer && !!section;
      const show = showSection || showAnswer || showIssue;

      return { section, answer, showSection, showAnswer, show, action, showIssue, issue, back: null };
    }
    case 'back':
      if (state?.showIssue) {
        return {
          ...state,
          issue: null,
          showIssue: false,
          showAnswer: !!state?.answer,
          show: !!state?.answer,
          back: state?.issue
        };
      }
      if (state?.showAnswer)
        return {
          ...state,
          answer: null,
          showSection: !!state?.section,
          show: !!state?.section,
          showAnswer: false,
          action: null,
          back: state?.answer
        };
      return {...initCurrentView, back: state?.back};
    default:
      return state;
  }
};

const SelectAuditViewContext = createContext({
  currentView: null,
  updateCurrentView: (val) => val,
  section: null,
  answer: null,
  goBack: (val) => val,
  activeSection: null,
  goToSection: (val) => val,
});

const useSelectAuditView = () => {
  const context = useContext(SelectAuditViewContext);
  if (context === undefined) {
    throw new Error('useSelectAuditView must be used within a AuditDataProvider');
  }

  return context;
};

const SelectAuditViewProvider = ({ ...props }) => {
  const [currentView, dispatch] = useReducer(reducer, initCurrentView);
  const { getSectionData } = useAuditData();

  const updateCurrentView = useCallback((section, answer, action, issue) => {
    dispatch({ type: 'update', value: { section, answer, action, issue } });
  }, []);

  const goBack = useCallback(() => {
    dispatch({ type: 'back' });
  }, []);

  const { section, answer, issue } = useMemo(() => getSectionData(currentView), [getSectionData, currentView]);

  const activeSection = useMemo(() => {
    return currentView?.answer || currentView?.section;
  }, [currentView]);

  const goToSection = useCallback((item) => {
    const section = isSection(item) ? item?._id : item?.section;
    const question = !isSection(item) && item?._id;
    updateCurrentView(section, question);
  }, [updateCurrentView]);

  return (
    <SelectAuditViewContext.Provider
      value={ {
        currentView,
        updateCurrentView,
        section,
        answer,
        issue,
        goBack,
        activeSection,
        goToSection
      } }
      { ...props }
    />
  );
};

const useShowAuditView = (questionId) => {
  const { isTablet } = useResponsive();
  const { currentView } = useSelectAuditView();

  return useMemo(() => {
    let isActive = false;
    let isSection = false;
    let isAnswer = false;
    let isNote = false;
    let isIncidences = false;
    let isIssue = false;
    const isCurrentView = questionId === currentView?.answer;

    if (isTablet || questionId === currentView?.answer) {
      if (isTablet) {
        isActive = currentView?.show;
        isSection = currentView?.showSection;
        isAnswer = currentView?.showAnswer;
        isIssue = currentView?.showIssue;
      }
      isNote = currentView?.action === 'note';
      isIncidences = currentView?.action === 'incidences';
    }

    return ({
      isActive,
      isSection,
      isAnswer,
      isNote,
      isIncidences,
      isIssue,
      isCurrentView
    });
  }, [isTablet, currentView, questionId]);
};

export { SelectAuditViewProvider, useSelectAuditView, useShowAuditView };

